import React from 'react';
import { withRouter } from "react-router";
import { API } from "aws-amplify";
import {Button, CircularProgress} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { toast } from "react-toastify";

import TableDisplay from '@/common/TableDisplay';
import { workflowTypes } from '@/utils/utils';
import exportFromJSON from "export-from-json";

const useStyles = makeStyles(
    theme => ({
        exportCandidatesButton: {
            backgroundColor: '#4a77f3',
            textTransform: 'uppercase',
            color: 'white',
            minWidth: '153px',
            touchAction: 'manipulate',
            fontWeight: 400,
            fontFamily: 'Arial',
            letterSpacing: '1.1px',
            fontSize: '12px',
            position: 'sticky',
            right: '16px',
            zIndex: 10,
        },
        searchAndNavigation: {
            '& button[aria-label="Clear Search"]': {
                color: 'var(--colorTitle)',
                '&.Mui-disabled': {
                    color: 'var(--colorSubtitle)',
                },
            },
            '& input::placeholder': {
                color: 'var(--colorSubtitle)',
                opacity: 1,
            },
            '& .MuiTablePagination-root .MuiIconButton-root': {
                color: 'var(--colorTitle)',
            },
            '& .MuiTablePagination-root .MuiSelect-icon': {
                color: 'var(--colorTitle)',
            }
        },
        candidatesTableWrapper: {
            overflowX: 'auto',
            maxWidth: '100%',
        }
    })
);

function CandidateIndex(props) {
    const [fields, setFields] = React.useState([]);
    const [candidates, setCandidates] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const [filter, setFilter] = React.useState({});

    const CLASSES = useStyles();

    React.useEffect(() => {
        getCandidatesData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const prepareCandidates = (fields, fieldsValues) => {
        let fieldsData = fields.reduce((acc, d) => {
            acc[d.id] = d;
            return acc;
        }, {});

        let data = [];
        for (let conversationId in fieldsValues) {
            const conversationData = fieldsValues[conversationId];
            const candidate = conversationData?.fieldValues?.reduce((acc, d) => {
                const FIELD = fieldsData[d.conversationRecordsFieldId];
                if (!FIELD) return acc;
                let value = d.value || '-';
                if (FIELD.fieldName === 'Phone Number') {
                    if (value && !value.startsWith('+') && !value.startsWith('-')) {
                        value = `+${value}`;
                    }
                }
                acc[FIELD.fieldName] = value;
                return acc;
            }, {
                'creationDate': new Date(conversationData.createdAt)
            });
            data.push(candidate);
        }
        setCandidates(data);
    }

    const getCandidatesData = async () => {
        setLoading(true);
        try {
            const fieldsResponse = await API.get(
                'Core',
                `/api/v1/conversation-records/fields?workflowType=${workflowTypes.APPLICANT}`
            );

            if (!fieldsResponse || !Array.isArray(fieldsResponse) || fieldsResponse.length === 0) {
                toast.error("No field definitions found.", { position: "top-center" });
                setLoading(false);
                return;
            }
            setFields(fieldsResponse);
            try {
                const valuesResponse = await API.get(
                    'Core',
                    `/api/v1/conversation-records/fields/values`
                );

                if (!valuesResponse || typeof valuesResponse !== "object") {
                    toast.error("Invalid candidate values received.", { position: "top-center" });
                    setLoading(false);
                    return;
                }
                prepareCandidates(fieldsResponse, valuesResponse);
                setLoading(false);
                return;

            } catch (error) {
                setLoading(false);
                console.error("Error fetching candidate values:", error);
                toast.error("Failed to load candidate values.", { position: "top-center" });
                return;
            }

        } catch (error) {
            setLoading(false);
            console.error("Error fetching fields:", error);
            toast.error("Failed to load field definitions.", { position: "top-center" });
            return;
        }
    };

    const downloadRecords = () => {
        const csvFileName="candidatesRecords";
        const exportType=exportFromJSON.types.csv;
        const cand=candidates.filter(filterCandidate);
        const formattedData = cand.map(item => {
            const creationDate = new Date(item.creationDate);
            const formattedCreationDate = creationDate.toLocaleString('en-US', {
                month: 'numeric', // mm
                day: 'numeric', // dd
                year: 'numeric', // yyyy
                hour: '2-digit', // hh
                minute: '2-digit', // mm
                second: '2-digit', // ss
                hour12: true, // AM/PM
            });
            delete item.creationDate;
            item.creationDate = formattedCreationDate;

            const fieldNamesArray = fields.map(item => item.fieldName);
            fieldNamesArray.push("creationDate");
            const reorderedItem = {};
            for (const key of fieldNamesArray) {
                reorderedItem[key] = item[key];
            }
            const modifiedObject = {};
            for (const key in reorderedItem) {
                if (key === "Bachelor’s Degree") {
                    modifiedObject["Bachelor\'s Degree"] = reorderedItem[key];
                } else if (key === "creationDate") {
                    modifiedObject["Creation date"] = reorderedItem[key];
                } else {
                    modifiedObject[key] = reorderedItem[key];
                }
            }

            return modifiedObject;
        });
        exportFromJSON(
            {
                data: formattedData,
                fileName: csvFileName,
                exportType
            }
        );
    }

    const updateFilter = (event, filterName) => {

        if (filterName === 'From date' || filterName === 'To date') {
            const TODAY = new Date();
            const AFTER_TOMORROW = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() + 2);

            if (Date.parse(event.target.value) > AFTER_TOMORROW) {
                toast(`Invalid input for '${filterName}' filter (future date)`, { position: "top-center", type: "error" });
                return;
            }
        }

        const FILTER = { ...filter };

        FILTER[filterName] = event.target.value;

        setFilter(FILTER)
    }

    const filterCandidate = (c, i) => {
        let result = true;

        const FILTER_KEYS = ['Phone Number', 'Full Name']

        FILTER_KEYS.forEach(filterKey => {
            if (filter[filterKey]) {
                result &= c[filterKey]?.toString().toUpperCase().includes(filter[filterKey].toUpperCase());
            }
        })
        if (filter['From date']) {
            result &= (Date.parse(filter['From date']) <= c['creationDate'] || filter['From date'] <= c['creationDate']);
        }

        if (filter['To date']) {
            result &= (Date.parse(filter['To date']) >= c['creationDate'] || filter['To date'] >= c['creationDate']);
        }

        return result;
    }

    return (
        <div className="rootView surveysView">
            <div className="navBar">
                <div className="navTitle">
                    <div className="title">Candidates</div>
                </div>
                <div className="actions">
                    <div className="">

                    </div>
                    {candidates.length > 0 && <div>
                        <Button onClick={downloadRecords} className={CLASSES.exportCandidatesButton}>
                            Export records
                        </Button>
                    </div>}
                </div>
                <div className="separator"></div>
            </div>
            <div className="scrollView">
                <div className="surveysList tableView">
                    <div className="tableSection">
                        <div className="candidatesHeader">
                            <div className="candidatesTitle">Your Candidates</div>
                            <div className='candidatesFilter'>

                                <input type={'text'} onFocus={e => e.target.type = 'date'} onChange={event => updateFilter(event, 'From date')} className='searchInput candidatesSearchInput ' placeholder='From date' />
                                <input type={'text'} onFocus={e => e.target.type = 'date'} onChange={event => updateFilter(event, 'To date')} className='searchInput candidatesSearchInput' placeholder='To date' />
                                <input onChange={event => updateFilter(event, 'Phone Number')} className='searchInput candidatesSearchInput' type={'tel'} placeholder={'Candidate phone'} />
                                <input onChange={event => updateFilter(event, 'Full Name')} className='searchInput candidatesSearchInput' type={'text'} placeholder={'Candidate Full Name'} />
                            </div>
                        </div>
                        {loading ? (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '75vh',
                                    width: '100%'
                                }}
                            >
                                <CircularProgress />
                            </div>
                        ) : (
                        <div className={CLASSES.candidatesTableWrapper}>
                            <div className={CLASSES.searchAndNavigation}>
                                <TableDisplay
                                    title={''}
                                    data={candidates.filter(filterCandidate)}
                                    columns={[...fields.map(f => {
                                        return {
                                            field: f.fieldName,
                                            title: f.fieldName,
                                            size: 122, // Increased from 120 for better alignment
                                            minSize: 80,
                                            maxSize: 200
                                        }
                                    }), {
                                        field: 'creationDate',
                                        title: 'Creation date',
                                        size: 162, // Increased from 160
                                        render: e => (<span>{e.creationDate.toLocaleString()}</span>)
                                    }]}
                                    options={{
                                        uniformColumnWidths: true, // Flag for TableDisplay to use uniform sizing
                                        topPaddingOnly: true // Only top padding for candidates page
                                    }}
                                />
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default withRouter(CandidateIndex);